import { api } from '@/services/api/AxiosBaseService'
import { createCrudService } from '@/services/api/createCrudService'
import type { SingleResponse } from '@/types/api'

// ----------------------- Steps -----------------------
export interface CredentialingStep {
  id: string
  step: string
  order: number
  status: 'ATIVO' | 'INATIVO'
}
export const CredentialingStepsService = createCrudService<CredentialingStep>('/credentialing/steps', { searchField: 'step' })

// ----------------------- Types -----------------------
export interface CredentialingProviderType {
  id: string
  parentId: string | null
  type: string
  /** Caminho completo na árvore, ex.: "Exame > Exame de sangue > Hemograma". */
  path: string
  status: 'ATIVO' | 'INATIVO'
  parent?: Pick<CredentialingProviderType, 'id' | 'type'> | null
  children?: CredentialingProviderType[]
}
export const CredentialingTypesService = createCrudService<CredentialingProviderType>('/credentialing/types', { searchField: 'type' })

// ----------------------- Commission Structures -----------------------
export interface CredentialingCommissionRule {
  id: string
  commissionStructureId: string
  contractFrom: number
  contractTo: number | null
  contractAction: 'A PARTIR DE' | 'INTERVALO'
  value: number
  commissionType: 'VALOR FIXO'
  formatted?: { value?: string }
}
export interface CredentialingCommissionStructure {
  id: string
  name: string
  /** `null` = sem meta de vendas mensais. */
  monthlyContractSalesGoal: number | null
  status: 'ATIVO' | 'INATIVO'
  rules?: CredentialingCommissionRule[]
}
const credCommissionBase = createCrudService<CredentialingCommissionStructure>(
  '/credentialing/commission-structures',
  { searchField: 'name' },
)
export const CredentialingCommissionService = {
  ...credCommissionBase,
  async createRule(sid: string, payload: Omit<CredentialingCommissionRule, 'id' | 'commissionStructureId' | 'formatted'>) {
    const r = await api.post<SingleResponse<CredentialingCommissionRule>>(`/credentialing/commission-structures/${sid}/rules`, payload)
    return r.data.data
  },
  async destroyRule(sid: string, ruleId: string) {
    await api.delete(`/credentialing/commission-structures/${sid}/rules/${ruleId}`)
  },
}

// ----------------------- Providers -----------------------
export interface ProviderSpecialty {
  id: string
  specialty: string
  code: string | null
  pivotId: string
  priceInCents: number
  discountValueInCents: number
  copaymentInCents: number
  telemedicine: boolean
  formatted: { price: string; discount: string; copay: string; member: string }
}
export interface ProviderTest {
  id: string
  test: string
  code: string | null
  pivotId: string
  priceInCents: number
  discountValueInCents: number
  copaymentInCents: number
  formatted: { price: string; discount: string; copay: string; member: string }
}
export interface ProviderBankDetails {
  id?: string
  bankId: string | null
  beneficiary: string | null
  document: string | null
  agency: string | null
  account: string | null
  pixKey: string | null
  /** TRANSFERÊNCIA | PIX | DEPÓSITO */
  transaction: string | null
  /** PESSOA FÍSICA | PESSOA JURÍDICA (titular da conta) */
  type: string | null
  /** CORRENTE | POUPANÇA | SALÁRIO | CONJUNTA */
  accountType: string | null
  bank?: { id: string; code: string; name: string } | null
}
export interface CredentialingProvider {
  id: string
  name: string
  document: string
  type: string
  status: 'EM ANDAMENTO' | 'ACEITO' | 'REJEITADO'
  requiredBankDetails?: boolean
  observations?: string | null
  step?: CredentialingStep | null
  providerType?: CredentialingProviderType | null
  /** Caminho completo da categoria ("Pai > Filho > ...") quando carregado. */
  providerTypePath?: string | null
  parent?: { id: string; name: string } | null
  /** Contagens enviadas pelo backend na listagem (withCount). */
  specialtiesCount?: number
  testsCount?: number
  contacts?: { id: string; type: string; contact: string; formatted?: { contact?: string } }[]
  specialties?: ProviderSpecialty[]
  tests?: ProviderTest[]
  bankDetails?: ProviderBankDetails | null
  address?: {
    postalCode: string | null
    address: string | null
    number: string | null
    complement?: string | null
    district?: string | null
    city?: { id: string; city: string; state?: { id: string; abbreviation: string } } | null
  }
  formatted?: { document?: string; createdAt?: string }
}

/** Opção enxuta para o select de "Contrato principal". */
export interface ProviderMainContractOption {
  id: string
  name: string
  document: string
  type: string
}

/** Card do kanban — credenciado enriquecido + checagem de completude. */
export interface KanbanCard {
  id: string
  name: string
  document: string
  type: string
  providerType?: string | null
  requiredBankDetails: boolean
  createdByName?: string | null
  specialtiesCount: number
  testsCount: number
  contacts: { id: string; type: string; contact: string; formatted?: { contact?: string } }[]
  isComplete: boolean
  missing: string[]
  createdAt?: string | null
  formatted?: { document?: string; createdAt?: string }
}
export interface KanbanColumn {
  id: string
  step: string
  order: number
  providers: KanbanCard[]
}
/** Linha do resultado da busca de especialidades/exames entre credenciados. */
export interface SpecialtySearchRow {
  pivotId: string
  providerId: string
  providerName: string
  cityName: string | null
  state: string | null
  itemName: string
  priceInCents: number
  discountValueInCents: number
  copaymentInCents: number
  telemedicine: boolean | null
  formatted: { particular: string; amplaMais: string; valorAssociado: string; valorAmplaMais: string }
}

export interface ProviderContractItem {
  id: string
  contractSignedAt: string | null
  contractPath: string | null
  contractUrl: string | null
  fileName: string | null
  createdAt: string | null
}

const baseProviders = createCrudService<CredentialingProvider>('/credentialing/providers', { searchField: 'name' })

/** Credenciado que já usa um documento (matriz, filial ou cadastro em curso). */
export interface ProviderDocumentUsage {
  id: string
  name: string
  status: string
  city: string | null
  isBranch: boolean
}

export const CredentialingProvidersService = {
  ...baseProviders,

  async kanban(userId?: string): Promise<{ columns: KanbanColumn[]; canFilterByUser: boolean }> {
    const r = await api.get<{ data: KanbanColumn[]; meta?: { canFilterByUser?: boolean } }>(
      '/credentialing/steps/kanban',
      { params: { user_id: userId || undefined } },
    )
    return { columns: r.data.data, canFilterByUser: Boolean(r.data.meta?.canFilterByUser) }
  },

  /** Usuários com credenciamento EM ANDAMENTO (filtro do kanban). */
  async kanbanUsers(): Promise<{ id: string; name: string }[]> {
    const r = await api.get<{ data: { id: string; name: string }[] }>('/credentialing/steps/kanban-users')
    return r.data.data
  },

  /** Histórico de contratos (PDF) do credenciado. */
  async listContracts(providerId: string): Promise<ProviderContractItem[]> {
    const r = await api.get<{ data: ProviderContractItem[] }>(`/credentialing/providers/${providerId}/contracts`)
    return r.data.data
  },

  /** Envia/atualiza o contrato (PDF) do credenciado. */
  async uploadContract(providerId: string, file: File, signedAt?: string) {
    const fd = new FormData()
    fd.append('contract_file', file)
    if (signedAt) fd.append('contract_signed_at', signedAt)
    const r = await api.post<SingleResponse<CredentialingProvider>>(`/credentialing/providers/${providerId}/update-contract`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return r.data.data
  },

  /** Credenciados elegíveis a "Contrato principal" (sem parent vinculado). */
  /**
   * Quem já usa este documento — em qualquer situação, inclusive os que a
   * listagem padrão esconde (ela filtra por "Aceito"). Serve de aviso no
   * formulário; não impede o cadastro.
   */
  async documentUsage(document: string, exclude?: string): Promise<ProviderDocumentUsage[]> {
    const r = await api.get<{ data: ProviderDocumentUsage[] }>(
      '/credentialing/providers/document-usage',
      { params: { document, exclude: exclude || undefined } },
    )
    return r.data.data
  },

  async mainContractOptions(q?: string, exclude?: string) {
    const r = await api.get<{ data: ProviderMainContractOption[] }>(
      '/credentialing/providers/main-contract-options',
      { params: { q: q || undefined, exclude: exclude || undefined } },
    )
    return r.data.data
  },

  async updateStep(providerId: string, stepId: string) {
    const r = await api.post<SingleResponse<CredentialingProvider>>(`/credentialing/providers/${providerId}/update-step`, { step_id: stepId })
    return r.data.data
  },

  async approve(providerId: string) {
    const r = await api.post<SingleResponse<CredentialingProvider>>(`/credentialing/providers/${providerId}/approve`)
    return r.data.data
  },

  /** Copia as especialidades do contrato principal (parent). */
  async copySpecialties(providerId: string) {
    const r = await api.post<SingleResponse<CredentialingProvider>>(`/credentialing/providers/${providerId}/copy-specialties`)
    return r.data.data
  },
  /** Copia os exames do contrato principal (parent). */
  async copyTests(providerId: string) {
    const r = await api.post<SingleResponse<CredentialingProvider>>(`/credentialing/providers/${providerId}/copy-tests`)
    return r.data.data
  },
  async reject(providerId: string, reason: string) {
    const r = await api.post<SingleResponse<CredentialingProvider>>(`/credentialing/providers/${providerId}/reject`, { reason })
    return r.data.data
  },

  /** Transfere a titularidade (created_by) do credenciado para outro usuário. Admin-only. */
  async transfer(providerId: string, userId: string) {
    const r = await api.post<SingleResponse<CredentialingProvider>>(`/credentialing/providers/${providerId}/transfer`, { user_id: userId })
    return r.data.data
  },

  /** Remove uma especialidade/exame (pivot) do credenciado. */
  async removeSpecialtyOrTest(providerId: string, pivotId: string, type: 'specialty' | 'test') {
    await api.delete(`/credentialing/providers/${providerId}/specialty-or-test/${pivotId}`, { params: { type } })
  },

  /** Atualiza os valores de uma especialidade/exame (pivot) do credenciado. */
  async updateSpecialtyOrTest(providerId: string, pivotId: string, payload: {
    type: 'specialty' | 'test'
    price_in_cents?: number
    discount_value_in_cents?: number
    copayment_in_cents?: number
    telemedicine?: boolean
  }) {
    await api.put(`/credentialing/providers/${providerId}/specialty-or-test/${pivotId}`, payload)
  },

  /** Busca especialidades/exames entre credenciados (filtro por estado/cidade). */
  async specialtyTestSearch(params: { kind: 'specialty' | 'test'; item_id?: string; state_id?: string; city_ids?: string[] }) {
    const r = await api.get<{ data: SpecialtySearchRow[]; meta?: { truncated: boolean; limit: number } }>('/credentialing/providers/specialty-test-search', {
      params: {
        kind: params.kind,
        item_id: params.item_id || undefined,
        state_id: params.state_id || undefined,
        // `city_ids[]=a&city_ids[]=b` — o Laravel lê como lista.
        'city_ids[]': params.city_ids?.length ? params.city_ids : undefined,
      },
    })
    return { rows: r.data.data, truncated: !!r.data.meta?.truncated, limit: r.data.meta?.limit ?? r.data.data.length }
  },

  async addSpecialty(providerId: string, payload: { specialty_id: string; price_in_cents?: number; copayment_in_cents?: number; discount_value_in_cents?: number; telemedicine?: boolean }) {
    const r = await api.post<SingleResponse<CredentialingProvider>>(`/credentialing/providers/${providerId}/add-specialty`, payload)
    return r.data.data
  },
  async addTest(providerId: string, payload: { test_id: string; price_in_cents?: number; copayment_in_cents?: number; discount_value_in_cents?: number }) {
    const r = await api.post<SingleResponse<CredentialingProvider>>(`/credentialing/providers/${providerId}/add-test`, payload)
    return r.data.data
  },

  // ----------------------- Relatórios -----------------------
  async reportDashboard(from: string, to: string) {
    const r = await api.get<{ data: { total: number; inProgress: number; accepted: number; rejected: number } }>(
      '/credentialing/reports/dashboard', { params: { from, to } },
    )
    return r.data.data
  },
  async reportCommissions(from: string, to: string) {
    const r = await api.get<{ data: {
      collaborators: { userId: string; userName: string; approvedCount: number; earningsInCents: number; formatted: { earnings: string } }[]
      commissions: { providerId: string; providerName: string; userId: string | null; userName: string; isPrincipal: boolean; position: number | null; amountInCents: number; approvedAt: string | null; formatted: { amount: string; approvedAt: string } }[]
    } }>('/credentialing/reports/commissions', { params: { from, to } })
    return r.data.data
  },
  /** Mapa de credenciados ACEITO. Sem filtro de período. */
  /** Especialidades atendidas numa cidade (credenciados aceitos), com quantos atendem cada uma. */
  async reportMapCitySpecialties(cityId: string): Promise<{ id: string; specialty: string; providers: number; providerNames: string[] }[]> {
    const r = await api.get<{ data: { id: string; specialty: string; providers: number; providerNames: string[] }[] }>(
      `/credentialing/reports/map/cities/${cityId}/specialties`,
    )
    return r.data.data
  },

  async reportMap(stateId?: string) {
    const r = await api.get<{
      data: { id: string; name: string; abbreviation: string | null; providers: number; specialtiesCount: number; testsCount: number }[]
      level: 'state' | 'city'
    }>('/credentialing/reports/map', { params: { state_id: stateId || undefined } })
    return r.data
  },

  async dashboardStatusSummary() {
    const r = await api.get<{ data: { status: string; count: number }[] }>('/credentialing/providers/dashboard/status-summary')
    return r.data.data
  },
  async dashboardSpecialtiesAndTests() {
    const r = await api.get<{ data: { specialties: { id: string; name: string; providers: number }[]; tests: { id: string; name: string; providers: number }[] } }>('/credentialing/providers/dashboard/specialties-and-tests')
    return r.data.data
  },
  async dashboardRankingByCity() {
    const r = await api.get<{ data: { cityId: string; cityName: string; state: string; count: number }[] }>('/credentialing/providers/dashboard/ranking-by-city')
    return r.data.data
  },
}
